import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Send, Trash2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useApi, errMsg } from "@/lib/api";
import { useStream } from "@/lib/stream";
import { Markdown } from "@/components/ai/Markdown";

const EXAMPLES = ["Quelles pathologies progressent le plus ce mois-ci ?", "Quelles wilayas ont le moins de pédiatres dans l'annuaire ?", "Résume l'activité de l'application en 5 points."];

export const ChatPanel = () => {
  const api = useApi();
  const stream = useStream();
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const fresh = useRef(null);
  const loadSessions = useCallback(() => api("get", "/ai/sessions").then(setSessions).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => {
    if (sid && fresh.current === sid) { return; }
    if (sid) { api("get", `/ai/sessions/${sid}/messages`).then(setMsgs).catch((e) => toast.error(errMsg(e))); } else { setMsgs([]); }
  }, [sid, api]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs]);

  const ask = async (q) => {
    const question = (q || text).trim();
    if (!question || busy) { return; }
    setBusy(true);
    setText("");
    try {
      const id = sid || (await api("post", "/ai/sessions")).id;
      if (!sid) { fresh.current = id; setSid(id); }
      setMsgs((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "" }]);
      await stream(`/ai/sessions/${id}/message`, { text: question }, (full) => setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: full }]));
      loadSessions();
    } catch (e) {
      toast.error(e.message);
      setMsgs((m) => m.filter((x) => x.content));
    } finally { setBusy(false); }
  };
  const remove = async (id) => { try { await api("delete", `/ai/sessions/${id}`); if (id === sid) { setSid(null); } loadSessions(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="grid lg:grid-cols-[240px_1fr] gap-6" data-testid="ai-chat-panel">
      <aside className="bg-white border border-stone-200 rounded-lg p-3 space-y-1 h-fit max-h-[560px] overflow-y-auto">
        <Button variant="outline" className="w-full mb-2" onClick={() => { fresh.current = null; setSid(null); }} data-testid="ai-new-session"><Plus className="h-4 w-4 mr-1.5" /> Nouvelle conversation</Button>
        {sessions.map((s) => (
          <div key={s.id} className={`group flex items-center rounded-md text-sm ${s.id === sid ? "bg-stone-900 text-white" : "hover:bg-stone-100 text-stone-700"}`}>
            <button type="button" className="flex-1 truncate text-left px-3 py-2" onClick={() => { fresh.current = null; setSid(s.id); }} data-testid={`ai-session-${s.id}`}>{s.title}</button>
            <button type="button" className="p-2 opacity-60 hover:opacity-100" onClick={() => remove(s.id)} aria-label="Supprimer" data-testid={`ai-session-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </aside>
      <div className="bg-white border border-stone-200 rounded-lg flex flex-col min-h-[560px] min-w-0">
        <div className="flex-1 p-6 space-y-5 overflow-y-auto max-h-[480px]" data-testid="ai-messages">
          {msgs.length === 0 && (
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-stone-700"><Sparkles className="h-4 w-4 text-[#C2410C]" /> Posez une question sur les statistiques de Sehatic-Pedia (données anonymes).</p>
              <div className="flex flex-wrap gap-2">{EXAMPLES.map((e, i) => <button key={e} type="button" onClick={() => ask(e)} className="text-left text-sm rounded-full border border-stone-200 px-3 py-1.5 hover:border-[#C2410C] hover:text-[#C2410C] transition-colors duration-150" data-testid={`ai-example-${i}`}>{e}</button>)}</div>
            </div>
          )}
          {msgs.map((m, i) => (m.role === "user"
            ? <p key={i} className="ml-auto max-w-[80%] w-fit rounded-lg bg-stone-900 text-white px-4 py-2 text-sm" data-testid={`ai-msg-user-${i}`}>{m.content}</p>
            : <div key={i} className="max-w-[90%]">{m.content ? <Markdown testId={`ai-msg-assistant-${i}`}>{m.content}</Markdown> : <p className="text-sm text-stone-500 animate-pulse" data-testid="ai-thinking">Claude réfléchit…</p>}</div>))}
          <div ref={end} />
        </div>
        <form className="border-t border-stone-200 p-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); ask(); }}>
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Votre question…" className="resize-none"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }} data-testid="ai-input" />
          <Button type="submit" disabled={busy || !text.trim()} className="bg-[#C2410C] hover:bg-[#9A3412] text-white self-end" data-testid="ai-send"><Send className="h-4 w-4" /></Button>
        </form>
      </div>
    </div>
  );
};
